// Administration › Export complet des données (réversibilité : conditions générales art. 14, Data Act) : lancer l'export (après
// l'avertissement sur les données personnelles des salariés), suivre la préparation, télécharger l'archive ZIP (7 jours), vérifier
// son empreinte, l'effacer. Tout est appliqué et contrôlé par le SERVEUR (droits, société, un seul export à la fois, durée).
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Download, KeyRound, PackageCheck, ShieldAlert, Trash2, XCircle } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { dateHeure, pluriel } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, Chargement, Dialogue, EtatErreur, Panneau, Progression, useConfirmation, useNotifier } from '../../components/ui.tsx';
import { LIBELLES_STATUT_EXPORT, type StatutExport } from '@shared/export-complet.ts';

interface Export {
  id: string; statut: StatutExport; demande_le: string; demandeur: string | null; termine_le: string | null; expire_le: string | null;
  efface_le: string | null; progression: number | null; etape: string | null; taille_octets: number | null; sha256: string | null;
  nb_tables: number | null; nb_lignes: number | null; nb_fichiers: number | null; octets_fichiers: number | null; secrets_inclus: boolean;
  erreur: string | null; telechargements: number; dernier_telechargement_le: string | null; telechargeable: boolean;
}
interface EtatExport {
  exports: Export[]; en_cours: Export | null; jours_conservation: number; taille_max_octets: number; version_schema: string | null;
  secrets: { nombre: number; coffre: boolean; autorise: boolean; phrase_min: number };
}

const CLE = ['export-complet'];
const taille = (n: number | null) => {
  if (n === null) return '—';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} Ko`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`;
  return `${(n / 1024 ** 3).toFixed(n % 1024 ** 3 === 0 ? 0 : 2).replace('.', ',')} Go`;
};
const tonStatut = (s: StatutExport) => (s === 'pret' ? 'succes' : s === 'echec' ? 'danger' : s === 'en_file' || s === 'en_cours' ? 'accent' : 'neutre') as 'succes' | 'danger' | 'accent' | 'neutre';

function DialogueLancement({ e, fermer, lance }: { e: EtatExport; fermer: () => void; lance: (n: EtatExport) => void }) {
  const [lu, setLu] = useState(false);
  const [avecSecrets, setAvecSecrets] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const s = e.secrets;
  const secretsPossibles = s.nombre > 0 && s.coffre && s.autorise;
  const phraseCourte = avecSecrets && phrase.length < s.phrase_min;
  const differente = avecSecrets && confirmation !== phrase;
  const lancer = async () => {
    setErreur(null);
    if (!lu) { setErreur('Cochez la case pour confirmer avoir lu l’avertissement.'); return; }
    if (phraseCourte) { setErreur(`La phrase de passe doit faire ${s.phrase_min} caractères au moins.`); return; }
    if (differente) { setErreur('Les deux phrases de passe ne sont pas identiques.'); return; }
    setEnvoi(true);
    try {
      const r = await api.post<{ id: string; etat: EtatExport }>('/api/admin/export-complet', { avertissementLu: true, secrets: avecSecrets ? { phrase } : null });
      lance(r.etat);
    } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Lancer l’export complet" ouvert fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" icone={<Archive aria-hidden />} chargement={envoi} disabled={!lu} onClick={lancer}>Lancer l’export</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Alerte ton="ambre" titre="L’archive contient des données personnelles de vos salariés">
          Noms, adresses et téléphones professionnels des comptes, temps passés nominatifs, auteurs des saisies de chantier, journal
          d’audit avec adresses IP. Votre société en est responsable : conservez l’archive en lieu sûr (support chiffré, accès
          restreint), ne la transmettez qu’à qui en a besoin, et effacez-la quand elle n’est plus utile.
        </Alerte>
        <label className="case"><input type="checkbox" checked={lu} onChange={(x) => setLu(x.target.checked)} />
          <span className="petit">J’ai lu cet avertissement : je conserverai l’archive en lieu sûr et l’effacerai quand elle ne sera plus utile.</span></label>
        <p className="petit secondaire" style={{ margin: 0 }}>Contenu : toutes les tables de votre société en JSON (et en CSV pour les tableurs), tous les
          fichiers d’origine (documents, photos, PV, pièces, PDF émis), un manifeste avec l’empreinte SHA-256 de chaque fichier et une
          notice qui décrit chaque table et chaque colonne. Mots de passe, jetons de connexion et clés d’API ne sont jamais exportés.</p>
        <label className="case"><input type="checkbox" checked={avecSecrets} disabled={!secretsPossibles} onChange={(x) => setAvecSecrets(x.target.checked)} />
          <span className="petit">Joindre les secrets d’installation des sites ({s.nombre}), chiffrés par une phrase de passe
            {!secretsPossibles && <span className="discret"> — {s.nombre === 0 ? 'aucun secret enregistré' : !s.coffre ? 'coffre de l’installation non configuré' : 'droit « lire les secrets » requis'}</span>}</span></label>
        {avecSecrets && (
          <div className="pile" style={{ gap: 'var(--e-2)' }}>
            <Champ libelle="Phrase de passe" type="password" autoComplete="new-password" value={phrase} onChange={(x) => setPhrase(x.target.value)}
              aide={`${s.phrase_min} caractères au moins. Notez-la : elle n’est enregistrée nulle part, et sans elle les secrets sont illisibles (même pour l’éditeur).`} />
            <Champ libelle="Confirmez la phrase de passe" type="password" autoComplete="new-password" value={confirmation} onChange={(x) => setConfirmation(x.target.value)}
              erreur={confirmation && differente ? 'Les deux phrases ne sont pas identiques.' : null} />
          </div>
        )}
      </div>
    </Dialogue>
  );
}

function CarteArchive({ x, effacer }: { x: Export; effacer: (x: Export) => void }) {
  return (
    <div className="pile" style={{ gap: 'var(--e-3)' }}>
      <dl className="fiche">
        <div><dt>Préparée le</dt><dd>{dateHeure(x.termine_le)}{x.demandeur ? ` — demandée par ${x.demandeur}` : ''}</dd></div>
        <div><dt>Téléchargeable jusqu’au</dt><dd>{dateHeure(x.expire_le)}</dd></div>
        <div><dt>Taille</dt><dd className="chiffre">{taille(x.taille_octets)}</dd></div>
        <div><dt>Contenu</dt><dd>{pluriel(x.nb_tables ?? 0, 'table')}, {pluriel(x.nb_lignes ?? 0, 'ligne')}, {pluriel(x.nb_fichiers ?? 0, 'fichier')} ({taille(x.octets_fichiers)})
          {x.secrets_inclus ? ', secrets d’installation chiffrés' : ''}</dd></div>
        <div style={{ gridColumn: '1 / -1' }}><dt>Empreinte SHA-256 de l’archive</dt><dd className="mono petit" data-empreinte>{x.sha256}</dd></div>
        <div><dt>Téléchargements</dt><dd>{x.telechargements ? `${x.telechargements} (dernier le ${dateHeure(x.dernier_telechargement_le)})` : 'aucun pour l’instant'}</dd></div>
      </dl>
      <div><Bouton variante="fantome" icone={<Trash2 aria-hidden />} onClick={() => effacer(x)}>Effacer l’archive maintenant</Bouton></div>
    </div>
  );
}

export function OngletExport() {
  const q = useQuery({
    queryKey: CLE, queryFn: () => api.get<EtatExport>('/api/admin/export-complet'),
    refetchInterval: (r) => (r.state.data?.en_cours ? 1500 : false),
  });
  const qc = useQueryClient();
  const notifier = useNotifier();
  const { confirmer, element } = useConfirmation();
  const [dialogue, setDialogue] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const e = q.data!;
  const enCours = e.en_cours;
  const pret = e.exports.find((x) => x.telechargeable) ?? null;
  const annuler = async () => {
    if (!enCours) return;
    setEnvoi(true);
    try { qc.setQueryData(CLE, await api.post<EtatExport>(`/api/admin/export-complet/${enCours.id}/annuler`)); notifier('Annulation demandée.'); }
    catch (x) { notifier(messageErreur(x), true); } finally { setEnvoi(false); }
  };
  const effacer = async (x: Export) => {
    if (!(await confirmer('Effacer l’archive maintenant ?', 'Elle ne pourra plus être téléchargée. La trace de l’export (date, taille, empreinte) est gardée. Faites-le une fois l’archive récupérée et vérifiée.', 'Effacer l’archive'))) return;
    try { qc.setQueryData(CLE, await api.post<EtatExport>(`/api/admin/export-complet/${x.id}/effacer`)); notifier('Archive effacée.'); }
    catch (err) { notifier(messageErreur(err), true); }
  };
  const telecharge = () => { setTimeout(() => qc.invalidateQueries({ queryKey: CLE }), 1500); };
  return (
    <div className="pile">
      <Panneau titre="Export complet des données">
        <div className="pile">
          {enCours ? (
            <div className="pile" style={{ gap: 'var(--e-2)' }} aria-live="polite">
              <Alerte ton="info" titre="Préparation de l’archive en cours">Vous pouvez quitter cette page : la préparation continue sur le serveur.</Alerte>
              <Progression pct={enCours.progression ?? 0} libelle={`${enCours.progression ?? 0} % — ${enCours.etape ?? 'en attente'}`} />
              <div className="barre-actions"><Bouton variante="fantome" icone={<XCircle aria-hidden />} chargement={envoi} onClick={annuler}>Annuler l’export</Bouton></div>
            </div>
          ) : pret ? (
            <div className="barre-actions">
              <a className="btn btn-principal" href={`/api/admin/export-complet/${pret.id}/archive`} download onClick={telecharge}><Download aria-hidden />Télécharger l’archive</a>
              <Bouton icone={<Archive aria-hidden />} onClick={() => setDialogue(true)}>Nouvel export</Bouton>
            </div>
          ) : (
            <div className="barre-actions">
              <Bouton variante="principal" icone={<Archive aria-hidden />} onClick={() => setDialogue(true)}>Lancer l’export complet</Bouton>
            </div>
          )}
          <p className="petit secondaire" style={{ margin: 0 }}>Toutes les données de votre société, dans des formats ouverts (JSON, CSV, fichiers d’origine),
            pour les garder ou changer de logiciel. Préparée en arrière-plan, l’archive reste téléchargeable {e.jours_conservation} jours par un
            administrateur, puis elle est effacée ; un nouvel export remplace l’archive précédente. Taille maximale : {taille(e.taille_max_octets)} (au-delà, l’éditeur vous remet une copie).</p>
        </div>
      </Panneau>
      {pret && (
        <Panneau titre="Archive prête" actions={<Badge ton="succes" icone={<PackageCheck size={14} aria-hidden />}>Prête</Badge>}>
          <CarteArchive x={pret} effacer={effacer} />
        </Panneau>
      )}
      <Panneau serre titre="Vérifier l’archive reçue">
        <div className="pile petit" style={{ padding: 'var(--e-3) var(--e-5) var(--e-4)', gap: 'var(--e-2)' }}>
          <span>Comparez l’empreinte SHA-256 du fichier téléchargé à celle affichée ci-dessus : sous Windows,
            <span className="mono"> certutil -hashfile &lt;archive.zip&gt; SHA256</span>.</span>
          <span>Contrôle complet (chaque fichier, chaque table, aucune donnée d’une autre société, aucun secret) : l’archive contient
            <span className="mono"> outils/verifier-archive.mjs</span> — <span className="mono">node verifier-archive.mjs &lt;archive.zip&gt;</span>. Le mode d’emploi est dans LISEZMOI.txt.</span>
          <span className="discret"><ShieldAlert size={14} aria-hidden style={{ verticalAlign: 'middle' }} /> Mots de passe, jetons de connexion, clés d’API et clé du coffre
            ne sont jamais exportés{e.secrets.nombre ? ' ; les secrets d’installation, seulement chiffrés par votre phrase de passe' : ''}.
            {' '}<KeyRound size={14} aria-hidden style={{ verticalAlign: 'middle' }} /> Version du schéma : {e.version_schema ?? '—'}.</span>
        </div>
      </Panneau>
      <Panneau serre titre="Derniers exports">
        {e.exports.length === 0 ? <p className="petit secondaire" style={{ margin: 'var(--e-4) var(--e-5)' }}>Aucun export pour l’instant.</p> : (
          <ul className="cons-liste" style={{ padding: 'var(--e-3) var(--e-5) var(--e-4)' }}>
            {e.exports.map((x) => (
              <li key={x.id}><span className="chiffre">{dateHeure(x.demande_le)}</span> — {x.demandeur ?? 'compte supprimé'} —{' '}
                <Badge ton={tonStatut(x.statut)} icone={false}>{LIBELLES_STATUT_EXPORT[x.statut]}</Badge>
                {x.statut === 'pret' || x.statut === 'expire' ? <span className="petit discret"> {taille(x.taille_octets)}{x.telechargements ? `, ${pluriel(x.telechargements, 'téléchargement')}` : ''}</span> : null}
                {x.erreur && <span className="petit"> — {x.erreur}</span>}</li>
            ))}
          </ul>
        )}
      </Panneau>
      {dialogue && <DialogueLancement e={e} fermer={() => setDialogue(false)} lance={(n) => { qc.setQueryData(CLE, n); setDialogue(false); notifier('Export lancé : préparation en arrière-plan.'); }} />}
      {element}
    </div>
  );
}
